import {
  type CreateHTTPClientOptions,
  createHTTPClient,
  queryStringAddon,
  valid,
} from '@medizzy/http-client';
import { createServiceHTTPClientProvider } from '@medizzy/service-utils';
import {
  type ServiceMethodParams,
  createBoundServiceMutationHookCreator,
  createBoundServiceQueryHooksCreator,
} from '@medizzy/service-utils';
import { type ZodSchema, z } from 'zod';

export function createMEDizzyHTTPClient(options?: CreateHTTPClientOptions) {
  return createHTTPClient(options).addon(valid()).addon(queryStringAddon);
}

export type MEDizzyHTTPClient = ReturnType<typeof createMEDizzyHTTPClient>;

export type APIMethodParams<Params = void> = ServiceMethodParams<
  MEDizzyHTTPClient,
  Params
>;

export const [HTTPClientProvider, useHTTPClient] =
  createServiceHTTPClientProvider<MEDizzyHTTPClient>({
    name: 'API',
  });

export const createAPIQueryHooks = createBoundServiceQueryHooksCreator({
  queryKeyPrefix: 'medizzy-api',
  useClient: useHTTPClient,
});

export const createAPIMutationHook = createBoundServiceMutationHookCreator({
  useClient: useHTTPClient,
});

export const paginationRequestParams = {
  page: z.number().nonnegative().optional(),
  size: z.number().nonnegative().optional(),
};

export function paginatedSchema<T extends ZodSchema>(content: T) {
  return z.object({
    page: z.object({
      number: z.number(),
      size: z.number(),
      totalElements: z.number(),
      totalPages: z.number(),
    }),
    content: z.array(content),
  });
}

export type PaginatedResponse<T> = {
  page: {
    number: number;
    size: number;
    totalElements: number;
    totalPages: number;
  };
  content: T[];
};
